import * as React from 'react';
import {InfiniteLoader, InfiniteLoaderProps} from 'react-virtualized';
import VirtualizedTable from './VirtualizedTable';
import VirtualizedList from './VirtualizedList';
import {RowDataType} from './redux/actions';
import {VirtualizedComponentProps} from './consts';
import {sortTableRows} from './VirtualizedHelpers';

export interface AsyncVirtualizedProps<RowData extends RowDataType> extends VirtualizedComponentProps<RowData> {
  loadMoreRows: InfiniteLoaderProps['loadMoreRows'];
  variant?: 'table' | 'list';
}

class AsyncVirtualized<RowData extends RowDataType = RowDataType> extends React.PureComponent<
  AsyncVirtualizedProps<RowData>
> {
  static defaultProps: Partial<AsyncVirtualizedProps<RowDataType>> = {
    variant: 'table',
    virtualized: true,
  };

  async componentDidUpdate(prevProps: AsyncVirtualizedProps<RowData>) {
    const {data, virtualized} = this.props;
    if (virtualized && data.count() < prevProps.data.count() && data.isEmpty() && this.refs.infiniteLoader) {
      const infiniteLoaderRef: InfiniteLoader = this.refs.infiniteLoader as InfiniteLoader;
      infiniteLoaderRef.resetLoadMoreRowsCache(true);
    }
  }

  isRowLoaded = ({index}) => {
    const {data} = this.props;
    return data.has(index);
  };

  render() {
    const {variant, count, loadMoreRows, data, ...otherProps} = this.props;
    if (count === null) {
      return null;
    }
    let Component;
    switch (variant) {
      case 'table':
        Component = VirtualizedTable;
        break;
      case 'list':
        Component = VirtualizedList;
        break;
    }
    if (!this.props.virtualized || !loadMoreRows) {
      return (
        <Component count={count} data={sortTableRows(this.props.columns, data, this.props.sortProps)} {...otherProps} />
      );
    }
    return (
      <InfiniteLoader ref="infiniteLoader" isRowLoaded={this.isRowLoaded} loadMoreRows={loadMoreRows} rowCount={count}>
        {({onRowsRendered, registerChild}) => (
          <Component
            virtualizedComponentRef={registerChild}
            count={count}
            data={data}
            {...otherProps}
            onRowsRendered={params => {
              if (this.props.onRowsRendered) {
                this.props.onRowsRendered(params);
              }
              return onRowsRendered(params);
            }}
          />
        )}
      </InfiniteLoader>
    );
  }
}

export default AsyncVirtualized;
